// hardcoded values, need API calls
export const TICKET_CONFIG = {
  // Accept button loading duration 
  ACCEPT_LOADING_DURATION: 10000,

  // Snackbar display duration 
  SNACKBAR_DURATION: 5000,
  
  // Ignore options 
  IGNORE_OPTIONS: [
    { days: 30, label: '30 Days', subtitle: 'Ignore for 1 month' },
    { days: 60, label: '60 Days', subtitle: 'Ignore for 2 months' },
    { days: 90, label: '90 Days', subtitle: 'Ignore for 3 months' }
  ],
  
  // Mock CR number generation
  generateCRNumber: () => {
    //API call
    return Math.floor(1000 + Math.random() * 9000);
  },
  
  // Generate recommendations based on ticket impact and system
  generateRecommendations: (effortType, system, ticketId) => {
    // Check for ticket-specific recommendations first
    const ticketSpecific = {
      'RC-2047': [
        `# SAP HANA Critical Log Disk Issue – Resolution Guide

The current situation indicates a **critical risk** within your **SAP HANA environment** and may escalate into **operational downtime** or **data loss**.

To resolve a **full SAP log disk**:

- Verify successful log backups by checking the \`backup.log\` for errors.
- Free up disk space using the SQL command \`ALTER SYSTEM RECLAIM LOG\` or enable **automatic log segment reclamation** if your SAP HANA version supports it.
- If backups are failing, this can also fill the log volume. Address any **backup errors first** to allow log segments to be reused.

---

## 1. Verify that Log Backups Are Succeeding

- Navigate to: \`/usr/sap/<SID>/HDB<Instance#>/<Host>/trace\` and check the \`backup.log\` file for any **"ERROR"** messages.
- In **HANA Studio**, check the \`M_BACKUP_CATALOG\` view to monitor backup success.
- If using **Backint** for log backups:
  - Inspect the \`backint.log\` for errors.
  - Contact the vendor if issues persist.

---

## 2. Free Up Space in the Transaction Log Volume

- Run SQL:
\`\`\`sql
ALTER SYSTEM RECLAIM LOG;
\`\`\`

- Alternatively, use the SAP HANA Cockpit interface to reclaim space.
- In HANA 2 SPS7 and later, configure the log disk usage reclaim threshold parameter to enable automatic reclamation.

## 3. Identify and Resolve Causes of Backup Failure

- Failures with third-party tools (e.g., Backint) can block log segment reuse, leading to disk full issues.
- Beyond disk capacity, issues like inode exhaustion or file system errors may also cause disk-full events.

## 4. Monitor and Add Disk Space

- Use SAP HANA Cockpit:
  - Monitor disk usage via the Alerts and Disk Usage tiles.
- If the underlying disk is truly full:
  - Add more physical storage or expand the volume.

## 5. Adjust SAP HANA Log Backup Volume Disk Usage Alert Thresholds

- Open SAP HANA Studio or SAP HANA Cockpit.
- Navigate to the alert configuration area → Log Backup Volume Disk Usage.
- Increase threshold parameters to:
  - Prevent false alarms during high-usage periods.
  - Still maintain sensitivity to genuine capacity risks.`
      ]
    };

    // If ticket-specific recommendations exist, return them
    if (ticketSpecific[ticketId]) {
      return ticketSpecific[ticketId];
    }

    // Fall back to the original logic
    const recommendations = {
      high: [
        'Escalate to senior technical team immediately',
        'Implement emergency rollback procedures if applicable',
        'Set up continuous monitoring for affected systems',
        'Notify all stakeholders and update status every 30 minutes',
        'Prepare detailed incident report for post-mortem analysis'
      ],
      medium: [
        'Assign to appropriate technical team within 2 hours',
        'Review recent changes that might have caused the issue',
        'Check system logs for error patterns and anomalies',
        'Test proposed solutions in staging environment first',
        'Document findings and resolution steps for knowledge base'
      ],
      low: [
        'Schedule resolution during next maintenance window',
        'Review similar past incidents for proven solutions',
        'Conduct thorough testing before implementing changes',
        'Update documentation and user guides if needed',
        'Consider automation opportunities to prevent recurrence'
      ]
    };
    
    const systemSpecific = {
      'Database': ['Check database connection pools', 'Review query performance metrics', 'Verify backup integrity'],
      'Frontend': ['Clear browser cache and CDN', 'Check API endpoint responses', 'Review client-side error logs'],
      'Security': ['Review access logs for anomalies', 'Update security patches immediately', 'Audit user permissions'],
      'Network': ['Check network latency and bandwidth', 'Review firewall rules and configurations', 'Test failover mechanisms']
    };
    
    const baseRecommendations = recommendations[effortType] || recommendations.medium;
    const systemRecommendations = systemSpecific[system] || [];
    
    return [...baseRecommendations.slice(0, 3), ...systemRecommendations.slice(0, 2)];
  },
  
  // API endpoints
  API: {
    ACCEPT_TICKET: '/api/tickets/accept',
    IGNORE_TICKET: '/api/tickets/ignore',
    UPDATE_STATUS: '/api/tickets/status'
  }
};

// Utility functions for API calls 
export const TicketAPI = {
  //API calls
  acceptTicket: async (ticketId) => {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          crNumber: TICKET_CONFIG.generateCRNumber(),
          newStatus: 'WORK IN PROGRESS'
        });
      }, TICKET_CONFIG.ACCEPT_LOADING_DURATION);
    });
  },
  
  ignoreTicket: async (ticketId, days) => {
    //API call
    console.log(`Ignoring ticket ${ticketId} for ${days} days`);
    return Promise.resolve({
      success: true,
      newStatus: 'Deferred',
      deferredDays: days
    });
  },
  
  updateTicketStatus: async (ticketId, status, metadata = {}) => {
    //API call
    console.log(`Updating ticket ${ticketId} status to ${status}`, metadata);
    return Promise.resolve({
      success: true,
      ticketId,
      status,
      metadata
    });
  }
};